import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X, Inbox, AlertTriangle, Shield, Clock, User, Mail } from "lucide-react";
import { backendApi } from "./api/backendApi";
import { toast } from "sonner";

const T = {
  green: '#1A3C2B', greenDeep: '#0E2118', greenTint: '#EDF3EF', greenGlow: 'rgba(26,60,43,0.1)',
  bg: '#F4F5F0', card: '#FFFFFF', line: '#E3E2DC', lineSoft: '#EEEEE9',
  ink: '#111418', body: '#3A4149', muted: '#687079', faint: '#9BA3AB',
  success: '#2F7D4F', successTint: '#E8F3EC',
  danger: '#C0413D', dangerTint: '#FCEAEA',
  warning: '#B9822E', warningTint: '#FBF1E1',
  info: '#3B6FA6', infoTint: '#EAF1FB',
};

const fmt = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
};

const StatBadge = ({ count, label, color, bg }) => (
  <div style={{
    background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 14,
    padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  }}>
    <span style={{ fontSize: 26, fontWeight: 800, color: T.ink, letterSpacing: '-0.02em' }}>{count}</span>
    <span style={{ fontSize: 11.5, fontWeight: 600, color: T.muted }}>{label}</span>
    <span style={{ display: 'inline-block', width: 24, height: 3, borderRadius: 99, background: color, marginTop: 2 }} />
  </div>
);

export default function AccessRequests() {
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["access-requests"],
    queryFn: () => backendApi.auth.listAccessRequests(),
    initialData: [],
  });

  const { data: deletionRequests = [] } = useQuery({
    queryKey: ['deletion-requests'],
    queryFn: () => backendApi.deletionRequests.list(),
    initialData: [],
  });

  const approve = useMutation({
    mutationFn: (id) => backendApi.auth.approveAccessRequest(id),
    onMutate: (id) => setBusyId(id),
    onSuccess: () => { toast.success("Access approved"); },
    onSettled: () => { setBusyId(null); queryClient.invalidateQueries({ queryKey: ["access-requests"] }); },
  });

  const reject = useMutation({
    mutationFn: (id) => backendApi.auth.rejectAccessRequest(id),
    onMutate: (id) => setBusyId(id),
    onSuccess: () => { toast.error("Request rejected"); },
    onSettled: () => { setBusyId(null); queryClient.invalidateQueries({ queryKey: ["access-requests"] }); },
  });

  const approveDeletion = useMutation({
    mutationFn: (id) => backendApi.deletionRequests.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deletion-requests'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success("User removed successfully");
    },
    onError: (e) => toast.error(e.message || "Could not approve removal"),
  });

  const rejectDeletion = useMutation({
    mutationFn: (id) => backendApi.deletionRequests.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deletion-requests'] });
      toast.success("Removal request rejected, user kept");
    },
    onError: (e) => toast.error(e.message || "Could not reject removal"),
  });

  return (
    <div style={{ background: T.bg, minHeight: '100%', padding: '28px 28px' }}>
      <style>{`
        .ar-row { transition: background 0.15s; }
        .ar-row:hover { background: #F7F8F5 !important; }
        .ar-btn { transition: all 0.15s; cursor: pointer; }
        .ar-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
        .ar-btn:active:not(:disabled) { transform: translateY(0); }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 16,
            background: T.green, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 16px ${T.greenGlow}`,
          }}>
            <Shield size={24} color="#fff" strokeWidth={1.8} />
          </div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.02em' }}>
              Access Requests
            </h2>
            <p style={{ fontSize: 12.5, color: T.muted, margin: '3px 0 0' }}>
              Review and manage HRMS access for pending users
            </p>
          </div>
        </div>
        {/* Stats row */}
        <div style={{ display: 'flex', gap: 12 }}>
          <StatBadge count={requests.length} label="Pending Access" color={T.warning} />
          <StatBadge count={deletionRequests.length} label="Removal Requests" color={T.danger} />
        </div>
      </div>

      {/* Deletion alerts */}
      {deletionRequests.length > 0 && (
        <div style={{
          background: '#FFFBF2', border: `1.5px solid #F0DBB0`,
          borderRadius: 16, padding: 20, marginBottom: 24,
          boxShadow: '0 2px 8px rgba(185,130,46,0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: T.warningTint, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={16} color={T.warning} strokeWidth={2} />
            </div>
            <div>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: '#6B4A10' }}>
                {deletionRequests.length} Removal Request{deletionRequests.length > 1 ? 's' : ''} Pending
              </span>
              <p style={{ fontSize: 11.5, color: '#A0722A', margin: '1px 0 0' }}>These require your approval before users are removed</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {deletionRequests.map((r) => (
              <div key={r.id} style={{
                background: T.card, border: '1px solid #F0DBB0', borderRadius: 12,
                padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: T.dangerTint, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <User size={16} color={T.danger} />
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: T.ink, margin: 0 }}>
                      {r.target_user_name || r.target_user_email}
                    </p>
                    <p style={{ fontSize: 11.5, color: T.muted, margin: '2px 0 0' }}>
                      Removal requested by {r.requested_by_name || "platform owner"}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="ar-btn" disabled={rejectDeletion.isLoading}
                    onClick={() => rejectDeletion.mutate(r.id)}
                    style={{
                      padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                      border: `1.5px solid ${T.line}`, background: T.card, color: T.muted,
                    }}>
                    Keep User
                  </button>
                  <button className="ar-btn"
                    disabled={approveDeletion.isLoading}
                    onClick={() => window.confirm(`Remove ${r.target_user_name || r.target_user_email}? This cannot be undone.`) && approveDeletion.mutate(r.id)}
                    style={{
                      padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                      border: 'none', background: T.danger, color: '#fff',
                    }}>
                    Approve Removal
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main table card */}
      <div style={{ background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1.5px solid ${T.lineSoft}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Inbox size={16} color={T.green} strokeWidth={2} />
          <h3 style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, margin: 0 }}>Pending Access Requests</h3>
          {requests.length > 0 && (
            <span style={{
              marginLeft: 'auto', background: T.warningTint, color: T.warning,
              borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 700,
            }}>
              {requests.length} pending
            </span>
          )}
        </div>

        {isLoading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ width: 36, height: 36, border: `3px solid ${T.greenTint}`, borderTopColor: T.green, borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <p style={{ color: T.faint, fontSize: 13 }}>Loading requests…</p>
          </div>
        ) : requests.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.lineSoft }}>
                  {['Requester', 'Email', 'Requested At', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '11px 20px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: T.faint, letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((r, i) => (
                  <tr key={r.id} className="ar-row"
                    style={{ borderBottom: i < requests.length - 1 ? `1px solid ${T.lineSoft}` : 'none' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 10,
                          background: T.greenTint, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 700, color: T.green,
                        }}>
                          {r.full_name?.[0] || 'U'}
                        </div>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{r.full_name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <Mail size={13} color={T.faint} />
                        <span style={{ fontSize: 13, color: T.body }}>{r.email}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <Clock size={13} color={T.faint} />
                        <span style={{ fontSize: 13, color: T.body }}>{fmt(r.created_at)}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="ar-btn" disabled={busyId === r.id}
                          onClick={() => approve.mutate(r.id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                            border: 'none', background: T.success, color: '#fff',
                            opacity: busyId === r.id ? 0.5 : 1,
                          }}>
                          <Check size={13} strokeWidth={2.5} /> Approve
                        </button>
                        <button className="ar-btn" disabled={busyId === r.id}
                          onClick={() => reject.mutate(r.id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                            border: `1.5px solid ${T.line}`, background: T.card, color: T.danger,
                            opacity: busyId === r.id ? 0.5 : 1,
                          }}>
                          <X size={13} strokeWidth={2.5} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '64px 20px' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 20, background: T.lineSoft,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <Inbox size={28} color={T.faint} strokeWidth={1.5} />
            </div>
            <p style={{ fontSize: 15, fontWeight: 700, color: T.ink, margin: '0 0 6px' }}>All clear</p>
            <p style={{ fontSize: 13, color: T.muted, margin: 0 }}>No pending access requests right now.</p>
          </div>
        )}
      </div>
    </div>
  );
}